import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react'
import type { ComponentType, HTMLAttributes } from 'react'
import { cn } from '../lib/cn'

export type StatusTone = 'verified' | 'warning' | 'critical' | 'info'

const TONE_STYLES: Record<StatusTone, string> = {
  verified: 'bg-state-verified-surface text-state-verified border-state-verified-border',
  warning: 'bg-state-warning-surface text-state-warning border-state-warning-border',
  critical: 'bg-state-critical-surface text-state-critical border-state-critical-border',
  info: 'bg-state-info-surface text-state-info border-state-info-border',
}

const TONE_ICONS: Record<StatusTone, ComponentType<{ className?: string }>> = {
  verified: CheckCircle2,
  warning: AlertTriangle,
  critical: XCircle,
  info: Info,
}

export interface StatusPillProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  tone: StatusTone
  /** Required. There is no colour-only rendering path. */
  label: string
  size?: 'sm' | 'md'
}

/**
 * F0-13 — the single approved way to render traffic-light state.
 *
 * §4.1 rule 3: `verified`/`warning`/`critical` are always paired with text AND an
 * icon, never colour alone. `label` is a required prop and there is no `children`
 * escape hatch, so the highest-risk screens for colour-only encoding — the
 * DRD-FR-004 Met / Partially Met / Not Met scorecard and the descriptive KPI
 * tiles — cannot regress into colour-only chips.
 */
export function StatusPill({ tone, label, size = 'md', className, ...rest }: StatusPillProps) {
  const Icon = TONE_ICONS[tone]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        size === 'sm' ? 'px-2 py-0.5 text-caption' : 'px-2.5 py-1 text-body-sm',
        TONE_STYLES[tone],
        className,
      )}
      {...rest}
    >
      <Icon aria-hidden className={size === 'sm' ? 'size-3' : 'size-3.5'} />
      {label}
    </span>
  )
}
